import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Html5Qrcode } from "html5-qrcode";

import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarDay,
  FaCamera,
  FaCheckCircle,
  FaClock,
  FaClipboardList,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaPlus,
  FaQrcode,
  FaSignOutAlt,
  FaTimes,
  FaUserCircle,
  FaThLarge,
  FaEnvelope,
  FaIdBadge,
  FaShieldAlt,
} from "react-icons/fa";

import "./QRPage.css";
import { API_URL } from "../../config";
import WorkerEntryEdit from "./WorkerEntryEdit";

const getEntryStatus = (entry) => {
  const status = String(entry.status || "erfasst").toLowerCase();

  const statuses = {
    erfasst: { label: "Erfasst", className: "erfasst" },
    gebucht: { label: "Gebucht", className: "gebucht" },
    archiviert: { label: "Archiviert", className: "archiviert" },
  };

  return {
    ...(statuses[status] || statuses.erfasst),
    edited: Boolean(entry.edited_at),
  };
};


const workTimeBerlinParts = (date) => Object.fromEntries(
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date).filter(part => part.type !== "literal")
    .map(part => [part.type, part.value])
);

const workTimeInput = (date) => {
  const p = workTimeBerlinParts(date);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

const workTimeToUtc = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    throw new Error("Bitte Beginn und Ende vollständig eingeben.");
  }
  const naive = new Date(value + ":00Z");
  if (Number.isNaN(naive.getTime()) || naive.toISOString().slice(0, 16) !== value) {
    throw new Error("Ungültiges Datum oder ungültige Uhrzeit.");
  }
  const offsets = new Set();
  for (const hours of [-36, -24, -12, 0, 12, 24, 36]) {
    const probe = new Date(naive.getTime() + hours * 3600000);
    const p = workTimeBerlinParts(probe);
    offsets.add(Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour), Number(p.minute), Number(p.second)
    ) - probe.getTime());
  }
  const matches = [...offsets].map(offset => new Date(naive.getTime() - offset))
    .filter(date => workTimeInput(date) === value);
  if (matches.length !== 1) {
    throw new Error("Diese Berliner Uhrzeit existiert wegen der Zeitumstellung nicht oder ist doppeldeutig. Bitte eine andere Uhrzeit wählen.");
  }
  return matches[0];
};

const workTimePayload = (startValue, endValue, pauseValue) => {
  const start = workTimeToUtc(startValue);
  const end = workTimeToUtc(endValue);
  if (String(pauseValue).trim() === "") throw new Error("Bitte die Pause in Minuten eingeben, auch wenn sie 0 beträgt.");
  const pause = Number(pauseValue);
  const duration = end - start;
  if (duration <= 0 || duration > 86400000) {
    throw new Error("Das Ende muss nach dem Beginn liegen. Ein Eintrag darf höchstens 24 Stunden umfassen.");
  }
  if (!Number.isInteger(pause) || pause < 0 || pause * 60000 >= duration) {
    throw new Error("Die Pause muss eine ganze, nicht negative Minutenzahl und kürzer als die Zeitspanne sein.");
  }
  if (end.getTime() > Date.now()) throw new Error("Das Arbeitsende darf nicht in der Zukunft liegen.");
  if (workTimeInput(start).slice(0, 7) !== workTimeInput(new Date(end.getTime() - 1)).slice(0, 7)) {
    throw new Error("Bitte Arbeitszeiten über eine Monatsgrenze in zwei Einträge aufteilen.");
  }
  return {
    time: start.toISOString().replace(".000Z", "Z"),
    started_at: start.toISOString().replace(".000Z", "Z"),
    ended_at: end.toISOString().replace(".000Z", "Z"),
    break_minutes: pause,
  };
};


const assignmentUtcDate = (value) => {
  const text = String(value);
  return new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)
    ? text.replace(" ", "T") + "Z" : text);
};

const assignmentShiftDay = (day, amount) => {
  const date = new Date(day + "T12:00:00Z");
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
};

const assignmentMonday = () => {
  const day = workTimeInput(new Date()).slice(0, 10);
  const weekday = new Date(day + "T12:00:00Z").getUTCDay();
  return assignmentShiftDay(day, -(weekday === 0 ? 6 : weekday - 1));
};

const assignmentDayLabel = (day) => new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin", weekday: "long", day: "2-digit", month: "2-digit", year: "numeric",
}).format(new Date(day + "T12:00:00Z"));

const assignmentTimeLabel = (value) => new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
}).format(assignmentUtcDate(value));

const EmployeeAssignments = ({ onCapture }) => {
  const [week, setWeek] = useState(assignmentMonday);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [showCancelled, setShowCancelled] = useState(false);
  const days = Array.from({ length: 7 }, (_, index) => assignmentShiftDay(week, index));

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setAssignments([]);

    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Bitte erneut anmelden.");
        const params = new URLSearchParams({
          start: workTimeToUtc(week + "T00:00").toISOString(),
          end: workTimeToUtc(assignmentShiftDay(week, 7) + "T00:00").toISOString(),
        });
        const response = await fetch(`${API_URL}/api/work-assignments?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(data)) {
          throw new Error(data?.error || "Deine Einsätze konnten nicht geladen werden.");
        }
        if (!controller.signal.aborted) setAssignments(data);
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [week, revision]);

  const move = (amount) => {
    setLoading(true);
    setWeek(previous => assignmentShiftDay(previous, amount));
  };

  return (
    <section className="qr-tab-panel">
      <div className="qr-page-heading">
        <div>
          <span className="qr-section-kicker">DEINE ARBEITSPLANUNG</span>
          <h2>Meine Termine</h2>
          <p>Hier siehst du die Aufträge, die dein Firmenadmin für dich geplant hat.</p>
          <p>{assignmentDayLabel(week)} bis {assignmentDayLabel(days[6])}</p>
          <small>Geplante Zeiten in Berlin. Tatsächliche Arbeitszeiten erfasst du im Arbeitsbereich.</small>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", margin: "18px 0" }}>
        <button type="button" className="qr-action-secondary" onClick={() => move(-7)}>Vorherige Woche</button>
        <button type="button" className="qr-action-secondary" onClick={() => {
          const monday = assignmentMonday();
          setLoading(true);
          if (week === monday) setRevision(value => value + 1);
          else setWeek(monday);
        }}>Aktuelle Woche</button>
        <button type="button" className="qr-action-secondary" onClick={() => move(7)}>Nächste Woche</button>
        <button type="button" className="qr-action-secondary" disabled={loading} onClick={() => {
          setLoading(true);
          setRevision(value => value + 1);
        }}>Aktualisieren</button>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
        <input type="checkbox" checked={showCancelled} onChange={event => setShowCancelled(event.target.checked)} />
        Stornierte Einsätze anzeigen
      </label>

      {error && <p role="alert" style={{ padding: "14px", borderRadius: "10px", color: "#a52c2c", background: "#fff0f0" }}>{error}</p>}
      {loading ? <p role="status">Deine Einsätze werden geladen …</p> : error ? null : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "16px" }}>
          {days.map(day => {
            const start = workTimeToUtc(day + "T00:00");
            const end = workTimeToUtc(assignmentShiftDay(day, 1) + "T00:00");
            const rows = assignments.filter(assignment =>
              (showCancelled || assignment.status === "geplant") &&
              assignmentUtcDate(assignment.planned_start) < end &&
              assignmentUtcDate(assignment.planned_end) > start
            );
            return (
              <section key={day} style={{ padding: "16px", border: "1px solid #dce6ef", borderRadius: "16px", background: "#f6f9fc" }}>
                <h3 style={{ margin: "0 0 14px" }}><FaCalendarDay aria-hidden="true" /> {assignmentDayLabel(day)}</h3>
                {!rows.length && <p style={{ color: "#65778a" }}>Keine Einsätze geplant.</p>}
                {rows.map(assignment => {
                  const startTime = assignmentUtcDate(assignment.planned_start);
                  const endTime = assignmentUtcDate(assignment.planned_end);
                  const net = (endTime - startTime) / 60000 - Number(assignment.planned_break_minutes);
                  return (
                    <article key={assignment.id} style={{ marginTop: "12px", padding: "16px", borderRadius: "12px", background: assignment.status === "storniert" ? "#edf0f3" : "#fff", border: "1px solid #dce6ef", overflowWrap: "anywhere" }}>
                      <strong>{assignment.project_name}</strong>
                      <p>Kunde: {assignment.customer_name}</p>
                      {assignment.project_address && <p style={{ whiteSpace: "pre-wrap" }}>Ort: {assignment.project_address}</p>}
                      <p><strong>Geplanter Beginn:</strong><br />{assignmentTimeLabel(assignment.planned_start)} Uhr</p>
                      <p><strong>Geplantes Ende:</strong><br />{assignmentTimeLabel(assignment.planned_end)} Uhr</p>
                      <p>Geplante Pause: {assignment.planned_break_minutes} Minuten</p>
                      <p>Geplante Arbeitszeit: {Math.floor(net / 60)} Std. {Math.round(net % 60)} Min.</p>
                      {assignment.notes && <p style={{ whiteSpace: "pre-wrap" }}><strong>Hinweise:</strong><br />{assignment.notes}</p>}
                      {assignment.status === "geplant" && (
                        <button type="button" className="qr-action-primary" onClick={() => onCapture(assignment)} style={{ margin: "10px 0", width: "100%" }}>
                          Zeit manuell erfassen
                        </button>
                      )}
                      <span style={{ fontWeight: 700, color: assignment.status === "storniert" ? "#a52c2c" : "#17699b" }}>
                        {assignment.status === "storniert" ? "Storniert" : "Geplant"}
                      </span>
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
};



const timerDurationLabel = (seconds) => {
  const value = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(value / 3600)).padStart(2, "0")}:${String(Math.floor(value / 60) % 60).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
};

const WorkTimerPanel = ({ assignment, onSaved }) => {
  const [timer, setTimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [success, setSuccess] = useState("");
  const [activityText, setActivityText] = useState("");
  const [clock, setClock] = useState(() => Date.now());
  const [serverOffset, setServerOffset] = useState(0);
  const busyRef = useRef(false);
  const requestVersion = useRef(0);
  const aliveRef = useRef(true);

  const loadCurrent = useCallback(async () => {
    if (busyRef.current) return;
    const version = ++requestVersion.current;
    const sent = Date.now();
    try {
      const response = await fetch(`${API_URL}/api/work-timers/current`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data || !Object.prototype.hasOwnProperty.call(data, "timer")) {
        throw new Error(data?.error || "Laufende Zeiterfassung konnte nicht geladen werden.");
      }
      if (!aliveRef.current || requestVersion.current !== version) return;
      setTimer(data.timer);
      setLoadError("");
      if (data.timer?.server_now) {
        setServerOffset(assignmentUtcDate(data.timer.server_now).getTime() - (sent + Date.now()) / 2);
      }
    } catch (error) {
      if (aliveRef.current && requestVersion.current === version) setLoadError(error.message);
    } finally {
      if (aliveRef.current && requestVersion.current === version) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void loadCurrent();
    const poll = setInterval(() => { void loadCurrent(); }, 15000);
    const clockTimer = setInterval(() => setClock(Date.now()), 1000);
    const refresh = () => { void loadCurrent(); };
    window.addEventListener("focus", refresh);
    return () => {
      aliveRef.current = false;
      requestVersion.current += 1;
      clearInterval(poll);
      clearInterval(clockTimer);
      window.removeEventListener("focus", refresh);
    };
  }, [loadCurrent]);

  const perform = async (action) => {
    if (busyRef.current || loading || loadError) return;
    let path;
    let body;
    if (action === "start") {
      if (!assignment || timer) return;
      path = "start";
      body = { assignment_id: assignment.id };
    } else {
      if (!timer) return;
      path = `${timer.id}/${action}`;
      body = {};
      if (action === "stop") {
        if (!activityText.trim()) {
          setActionError("Bitte die erledigte Tätigkeit vor dem Stoppen beschreiben.");
          return;
        }
        body.activity = activityText.trim();
      }
      if (action === "cancel") {
        const reason = window.prompt("Warum soll diese Zeiterfassung ohne Arbeitszeiteintrag abgebrochen werden?");
        if (reason === null) return;
        if (!reason.trim() || reason.trim().length > 1000) {
          setActionError("Bitte einen Abbruchgrund mit 1 bis 1000 Zeichen eingeben.");
          return;
        }
        body.reason = reason.trim();
      }
    }

    busyRef.current = true;
    requestVersion.current += 1;
    setBusy(true);
    setActionError("");
    setSuccess("");
    const sent = Date.now();
    try {
      const response = await fetch(`${API_URL}/api/work-timers/${path}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.success !== true || !data.timer) {
        throw new Error(data?.error || "Timeraktion wurde nicht bestätigt.");
      }
      if (!aliveRef.current) return;
      const active = ["laufend", "pausiert"].includes(data.timer.status);
      setTimer(active ? data.timer : null);
      setClock(Date.now());
      if (data.timer.server_now) {
        setServerOffset(assignmentUtcDate(data.timer.server_now).getTime() - (sent + Date.now()) / 2);
      }
      setSuccess({
        start: "Zeiterfassung gestartet.", pause: "Pause gestartet.",
        resume: "Arbeit fortgesetzt.", stop: "Arbeitszeit gespeichert. Sie wartet auf Prüfung durch den Firmenadmin.",
        cancel: "Zeiterfassung mit Grund abgebrochen; kein Arbeitszeiteintrag erstellt.",
      }[action]);
      if (action === "stop") {
        setActivityText("");
        try { await onSaved?.(); } catch {
          if (aliveRef.current) setSuccess("Arbeitszeit gespeichert. Bitte den Verlauf aktualisieren.");
        }
      }
    } catch (error) {
      if (aliveRef.current) setActionError(error instanceof TypeError
        ? "Keine Serverantwort erhalten. Bitte den Timerstatus aktualisieren, bevor du erneut klickst."
        : error.message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) {
        setBusy(false);
        void loadCurrent();
      }
    }
  };

  const effectiveNow = clock + serverOffset;
  const elapsed = timer ? Math.max(0, (effectiveNow - assignmentUtcDate(timer.started_at).getTime()) / 1000) : 0;
  const pause = timer ? Number(timer.paused_seconds) + (timer.status === "pausiert"
    ? Math.max(0, (effectiveNow - assignmentUtcDate(timer.pause_started_at).getTime()) / 1000) : 0) : 0;
  const disabled = loading || busy || Boolean(loadError);
  const projectName = timer?.project_name || (assignment?.id === timer?.assignment_id ? assignment?.project_name : "") || "Gestartetes Projekt";

  return (
    <section aria-label="Laufende Zeiterfassung" style={{ margin: "18px 0", padding: "20px", border: "1px solid #cbdff0", borderRadius: "16px", background: "#fff" }}>
      <h3 style={{ marginTop: 0 }}><FaClock aria-hidden="true" /> Start / Stopp</h3>
      {loading ? <p role="status">Timerstatus wird geladen …</p> : timer ? (
        <>
          <strong>{projectName}</strong>
          <p>{timer.status === "pausiert" ? "Pause läuft" : "Arbeit läuft"} · Beginn: {assignmentTimeLabel(timer.started_at)} Uhr</p>
          <p style={{ fontSize: "30px", fontWeight: 800, fontVariantNumeric: "tabular-nums", margin: "10px 0" }}>
            {timerDurationLabel(elapsed - pause)}
          </p>
          <p>Nettoarbeitszeit · Pause insgesamt: {timerDurationLabel(pause)}</p>
          <label htmlFor="timer-activity" className="qr-input-label">Erledigte Tätigkeit *</label>
          <textarea id="timer-activity" value={activityText} onChange={event => setActivityText(event.target.value)}
            maxLength={1000} rows={3} disabled={busy}
            placeholder="Was hast du bei diesem Einsatz erledigt?"
            style={{ display: "block", margin: "10px 0", padding: "12px", border: "1px solid #ccd9e5", borderRadius: "10px", width: "100%", boxSizing: "border-box", font: "inherit" }} />
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button type="button" className="qr-action-secondary" disabled={disabled}
              onClick={() => perform(timer.status === "pausiert" ? "resume" : "pause")}>
              {timer.status === "pausiert" ? "Arbeit fortsetzen" : "Pause starten"}
            </button>
            <button type="button" className="qr-action-primary" disabled={disabled || !activityText.trim()} onClick={() => perform("stop")}>
              Stopp & Arbeitszeit speichern
            </button>
            <button type="button" className="qr-action-secondary" disabled={disabled} onClick={() => perform("cancel")}>Mit Grund abbrechen</button>
          </div>
        </>
      ) : assignment ? (
        <>
          <strong>{assignment.project_name}</strong>
          <p>{assignment.customer_name} · Geplant: {assignmentTimeLabel(assignment.planned_start)} bis {assignmentTimeLabel(assignment.planned_end)} Uhr</p>
          <button type="button" className="qr-action-primary" disabled={disabled} onClick={() => perform("start")}>
            {busy ? "Wird gestartet …" : "Arbeit jetzt starten"}
          </button>
          <p><small>Die tatsächliche Arbeitszeit beginnt beim Klick auf Start.</small></p>
        </>
      ) : <p>Für heute ist kein eigener aktiver Einsatz zum Starten verfügbar.</p>}
      {loadError && <p role="alert" style={{ color: "#a52c2c" }}>{loadError}</p>}
      {actionError && <p role="alert" style={{ color: "#a52c2c" }}>{actionError}</p>}
      {success && <p role="status" style={{ color: "#177345" }}>{success}</p>}
      {(loadError || actionError) && <button type="button" className="qr-action-secondary" disabled={busy} onClick={() => { void loadCurrent(); }}>Timerstatus aktualisieren</button>}
    </section>
  );
};

const EmployeeAgenda = ({ onCapture, onOpen, onTimerSaved }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        const instant = Math.floor(Date.now() / 1000) * 1000;
        const params = new URLSearchParams({
          start: new Date(instant - 86400000).toISOString(),
          end: new Date(instant + 14 * 86400000).toISOString(),
        });
        const response = await fetch(`${API_URL}/api/work-assignments?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          signal: controller.signal,
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(data)) {
          throw new Error(data?.error || "Deine Termine konnten nicht geladen werden.");
        }
        if (!controller.signal.aborted) {
          setAssignments(data.filter(item => item.status === "geplant"));
          setNow(Date.now());
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [revision]);

  const today = workTimeInput(new Date(now)).slice(0, 10);
  const todayStart = workTimeToUtc(today + "T00:00").getTime();
  const tomorrowStart = workTimeToUtc(assignmentShiftDay(today, 1) + "T00:00").getTime();
  const current = assignments.filter(item =>
    assignmentUtcDate(item.planned_start).getTime() < tomorrowStart &&
    assignmentUtcDate(item.planned_end).getTime() > todayStart
  ).sort((a, b) => assignmentUtcDate(a.planned_start) - assignmentUtcDate(b.planned_start));
  const upcoming = assignments.filter(item =>
    assignmentUtcDate(item.planned_start).getTime() >= tomorrowStart
  ).sort((a, b) => assignmentUtcDate(a.planned_start) - assignmentUtcDate(b.planned_start));

  const latestStarted = [...current].filter(item => assignmentUtcDate(item.planned_start).getTime() <= now)
    .sort((a, b) => assignmentUtcDate(b.planned_start) - assignmentUtcDate(a.planned_start));
  const ongoing = latestStarted.find(item => assignmentUtcDate(item.planned_end).getTime() > now);
  const startCandidate = ongoing || latestStarted[0] || current[0] || null;

  const cards = (items, empty) => items.length ? items.map(item => (
    <article key={item.id} style={{ marginTop: "12px", padding: "16px", border: "1px solid #dce6ef", borderRadius: "12px", background: "#fff", overflowWrap: "anywhere" }}>
      <strong>{item.project_name}</strong>
      <p>Kunde: {item.customer_name}</p>
      <p>{assignmentTimeLabel(item.planned_start)} bis {assignmentTimeLabel(item.planned_end)} Uhr</p>
      <button type="button" className="qr-action-primary" onClick={() => onCapture(item)}>Zeit manuell erfassen</button>
    </article>
  )) : <p>{empty}</p>;

  return (
    <section style={{ margin: "24px 0" }} aria-label="Deine aktuellen und bevorstehenden Einsätze">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <h3><FaCalendarDay aria-hidden="true" /> Termine im Überblick</h3>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button type="button" className="qr-action-secondary" disabled={loading} onClick={() => setRevision(value => value + 1)}>Aktualisieren</button>
          <button type="button" className="qr-action-primary" onClick={onOpen}>Alle Termine öffnen</button>
        </div>
      </div>
      <small>Geplante Einsätze in Berliner Ortszeit. Die tatsächliche Arbeitszeit wird separat erfasst.</small>
      <WorkTimerPanel assignment={!loading && !error ? startCandidate : null} onSaved={onTimerSaved} />
      {loading ? <p role="status">Termine werden geladen …</p> : error ? <p role="alert" style={{ color: "#a52c2c" }}>{error}</p> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "18px", marginTop: "16px" }}>
          <section style={{ padding: "18px", borderRadius: "16px", background: "#eaf5ff" }}>
            <h4 style={{ margin: 0 }}>Heute ({current.length})</h4>
            {cards(current.slice(0, 2), "Heute ist kein Einsatz geplant.")}
            {current.length > 2 && <p>Weitere {current.length - 2} heutige Einsätze im Tab „Meine Termine“.</p>}
          </section>
          <section style={{ padding: "18px", borderRadius: "16px", background: "#f6f9fc" }}>
            <h4 style={{ margin: 0 }}>Ab morgen ({upcoming.length})</h4>
            {cards(upcoming.slice(0, 1), "In den nächsten 14 Tagen sind keine weiteren Einsätze geplant.")}
            {upcoming.length > 1 && <p>Weitere {upcoming.length - 1} bevorstehende Einsätze im Tab „Meine Termine“.</p>}
          </section>
        </div>
      )}
    </section>
  );
};

const QRPage = ({ onLogout }) => {
  // ==========================================
  // STATE
  // ==========================================

  const [activeTab, setActiveTab] = useState("overview");
  const [editingEntry, setEditingEntry] = useState(null);
  const [activity, setActivity] = useState("");
  const [workStartedAt, setWorkStartedAt] = useState("");
  const [workEndedAt, setWorkEndedAt] = useState("");
  const [workBreakMinutes, setWorkBreakMinutes] = useState("0");
  const [workTimeError, setWorkTimeError] = useState("");
  const [entries, setEntries] = useState([]);

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [modalStep, setModalStep] = useState(1);
  const [showScanner, setShowScanner] = useState(false);

  const [loadingEntries, setLoadingEntries] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // ==========================================
  // SCANNER REFERENCES
  // ==========================================

  const scannerRef = useRef(null);
  const scannerSessionRef = useRef(0);
  const scannerStartRef = useRef(null);
  const scanLockedRef = useRef(false);
  const scanCallbackRef = useRef(null);

  // ==========================================
  // USER INFORMATION
  // ==========================================

  const userId = localStorage.getItem("userId");

const fullName = [
  localStorage.getItem("first_name"),
  localStorage.getItem("last_name"),
]
  .filter(Boolean)
  .join(" ")
  .trim();

const username =
  fullName ||
  localStorage.getItem("username") ||
  localStorage.getItem("name") ||
  "Benutzer";

  const userEmail =
    localStorage.getItem("email") ||
    "Keine E-Mail hinterlegt";

  // ==========================================
  // STATUS MESSAGES
  // ==========================================

  const showStatus = useCallback((type, text) => {
    setMessage({ type, text });
  }, []);

  useEffect(() => {
    if (!message) return undefined;

    const timer = setTimeout(() => {
      setMessage(null);
    }, 4500);

    return () => clearTimeout(timer);
  }, [message]);

  // ==========================================
  // QR-CODE AUSWERTEN
  // ==========================================

  const parseLocationFromQR = useCallback((decodedText) => {
    try {
      const url = new URL(decodedText);

      return (
        url.searchParams.get("location") ||
        url.searchParams.get("ort") ||
        decodedText
      );
    } catch {
      return decodedText;
    }
  }, []);

  // ==========================================
  // EINTRÄGE LADEN
  // ==========================================

  const fetchEntries = useCallback(async () => {
    const storedUserId = localStorage.getItem("userId");

    if (!storedUserId) {
      setLoadingEntries(false);
      showStatus("error", "Benutzer-ID wurde nicht gefunden.");
      return;
    }

    setLoadingEntries(true);

    try {
      const response = await fetch(
        `${API_URL}/api/entries/${encodeURIComponent(storedUserId)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Einträge konnten nicht geladen werden.");
      }

      const data = await response.json();

      const sortedEntries = Array.isArray(data)
        ? [...data].sort(
            (a, b) => new Date(b.time) - new Date(a.time)
          )
        : [];

      setEntries(sortedEntries);
    } catch (error) {
      console.error("Fehler beim Laden der Einträge:", error);

      showStatus(
        "error",
        "Der Verlauf konnte nicht geladen werden."
      );
    } finally {
      setLoadingEntries(false);
    }
  }, [showStatus]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // ==========================================
  // EINTRAG SPEICHERN
  // ==========================================

  const saveEntryToServer = useCallback(
    async (entry) => {
      setSaving(true);

      try {
        const response = await fetch(`${API_URL}/api/entries`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(entry),
        });

        const responseData = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            responseData?.error ||
              "Eintrag konnte nicht gespeichert werden."
          );
        }

        const savedEntry = {
          ...entry,
          ...responseData,
        };

        setEntries((previous) =>
          [savedEntry, ...previous].sort(
            (a, b) => new Date(b.time) - new Date(a.time)
          )
        );

        showStatus(
          "success",
          "Tätigkeit erfolgreich erfasst und gespeichert."
        );

        if ("vibrate" in navigator) {
          navigator.vibrate([100, 50, 100]);
        }

        return true;
      } catch (error) {
        console.error("Fehler beim Speichern:", error);

        showStatus(
          "error",
          error.message || "Eintrag konnte nicht gespeichert werden."
        );

        return false;
      } finally {
        setSaving(false);
      }
    },
    [showStatus]
  );

  // ==========================================
  // SCANNER STOPPEN
  // ==========================================

  const stopScanner = useCallback(async () => {
    scannerSessionRef.current += 1;

    const scanner = scannerRef.current;
    const startPromise = scannerStartRef.current;

    scannerRef.current = null;
    scannerStartRef.current = null;

    if (scanner) {
      try {
        // Warten, falls die Kamera noch startet.
        if (startPromise) {
          await startPromise.catch(() => {});
        }

        if (scanner.isScanning) {
          await scanner.stop();
        }

        scanner.clear();
      } catch (error) {
        console.warn("Scanner konnte nicht vollständig beendet werden:", error);
      }
    }

    setShowScanner(false);
  }, []);

useEffect(() => {
  if (
    !showActivityModal ||
    localStorage.getItem("environment") !== "demo"
  ) {
    return;
  }

  const controller = new AbortController();
  setProjectsLoading(true);
  setProjectsError("");
  setProjects([]);

  const loadProjects = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/work-sites/available`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          signal: controller.signal,
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || !Array.isArray(data)) {
        throw new Error(
          data?.error || "Projekte konnten nicht geladen werden."
        );
      }

      if (!controller.signal.aborted) setProjects(data);
    } catch (error) {
      if (!controller.signal.aborted) {
        setProjectsError(error.message);
      }
    } finally {
      if (!controller.signal.aborted) setProjectsLoading(false);
    }
  };

  loadProjects();
  return () => controller.abort();
}, [showActivityModal]);

const createWorkTimePayload = useCallback(() => {
  if (localStorage.getItem("environment") !== "demo") {
    return { time: new Date().toISOString() };
  }
  return workTimePayload(workStartedAt, workEndedAt, workBreakMinutes);
}, [workStartedAt, workEndedAt, workBreakMinutes]);

let netWorkMinutes = null;
try {
  const start = workTimeToUtc(workStartedAt);
  const end = workTimeToUtc(workEndedAt);
  const pause = Number(workBreakMinutes);
  if (workBreakMinutes.trim() !== "" && Number.isInteger(pause) && pause >= 0 && end > start && pause * 60000 < end - start) {
    netWorkMinutes = (end - start) / 60000 - pause;
  }
} catch {
  // Incomplete form fields have no duration preview.
}

const saveSelectedProject = async () => {
  if (saving || scanLockedRef.current) return;

  const siteId = Number(selectedProjectId);

  if (
    !activity.trim() ||
    !Number.isSafeInteger(siteId) ||
    siteId <= 0 ||
    !projects.some((project) => Number(project.id) === siteId)
  ) {
    showStatus("error", "Bitte Tätigkeit und Projekt auswählen.");
    return;
  }

  let timeData;
  try {
    timeData = createWorkTimePayload();
    setWorkTimeError("");
  } catch (error) {
    setWorkTimeError(error.message);
    return;
  }

  scanLockedRef.current = true;

  try {
    await stopScanner();

    const saved = await saveEntryToServer({
      site_id: siteId,
      activity: activity.trim(),
      ...timeData,
    });

    if (saved) {
      setActivity("");
      setSelectedProjectId("");
      setModalStep(1);
      setShowActivityModal(false);
    }
  } catch (error) {
    setWorkTimeError(error.message);
  } finally {
    scanLockedRef.current = false;
  }
};

  // ==========================================
  // QR-CODE ERFOLGREICH GESCANNT
  // ==========================================

const handleScan = useCallback(
  async (decodedText) => {
    if (scanLockedRef.current) return;
    scanLockedRef.current = true;

    try {
      await stopScanner();

      if (
        !Number(localStorage.getItem("userId")) ||
        !activity.trim()
      ) {
        showStatus("error", "Bitte anmelden und eine Tätigkeit eingeben.");
        return;
      }

      const timeData = createWorkTimePayload();
      const text = String(decodedText).trim();
      const projectMatch = /^qroof:project:([1-9]\d*)$/.exec(text);
      let entry;

      if (projectMatch) {
        const siteId = Number(projectMatch[1]);

        if (
          !Number.isSafeInteger(siteId) ||
          localStorage.getItem("environment") !== "demo"
        ) {
          showStatus("error", "Ungültiger Projekt-QR-Code.");
          return;
        }

        entry = {
          site_id: siteId,
          activity: activity.trim(),
          ...timeData,
        };
      } else {
        if (text.toLowerCase().startsWith("qroof:")) {
          showStatus("error", "Dieser QRoof-QR-Code ist ungültig.");
          return;
        }

        const location = parseLocationFromQR(text);

        if (
          typeof location !== "string" ||
          !location.trim() ||
          location.trim().length > 500
        ) {
          showStatus("error", "Der QR-Code enthält keinen gültigen Arbeitsort.");
          return;
        }

        entry = {
          code: location.trim(),
          activity: activity.trim(),
          ...timeData,
        };
      }

      const saved = await saveEntryToServer(entry);

      if (saved) {
        setActivity("");
        setSelectedProjectId("");
        setModalStep(1);
        setShowActivityModal(false);
      }
    } catch (error) {
      setWorkTimeError(error.message);
      showStatus("error", error.message);
    } finally {
      scanLockedRef.current = false;
    }
  },
  [
    activity,
    createWorkTimePayload,
    parseLocationFromQR,
    saveEntryToServer,
    showStatus,
    stopScanner,
  ]
);

  scanCallbackRef.current = handleScan;

  // ==========================================
  // KAMERA STARTEN
  // ==========================================

  useEffect(() => {
    if (!showScanner || !showActivityModal || modalStep !== 2) {
      return undefined;
    }

    let cancelled = false;
    let scanner = null;
    let startPromise = null;

    const session = ++scannerSessionRef.current;

    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            "Kamerazugriff nicht verfügbar. Bitte HTTPS verwenden."
          );
        }

        const cameras = await Html5Qrcode.getCameras();

        if (cancelled || session !== scannerSessionRef.current) {
          return;
        }

        if (!cameras || cameras.length === 0) {
          throw new Error("Keine Kamera gefunden.");
        }

        const preferredCamera =
          cameras.find((camera) =>
            /back|rear|environment|rück|hinten/i.test(camera.label)
          ) || cameras[0];

        scanner = new Html5Qrcode("qr-reader", {
          verbose: false,
        });

        scannerRef.current = scanner;

        startPromise = scanner.start(
          preferredCamera.id,
          {
            fps: 10,
            qrbox: (width, height) => {
              const size = Math.max(
                100,
                Math.floor(Math.min(width, height) * 0.75)
              );

              return {
                width: size,
                height: size,
              };
            },
            disableFlip: false,
          },
          (decodedText) => {
            scanCallbackRef.current?.(decodedText);
          },
          () => {
            // Normale Scanfehler ignorieren.
          }
        );

        scannerStartRef.current = startPromise;

        await startPromise;

        if (cancelled || session !== scannerSessionRef.current) {
          if (scanner.isScanning) {
            await scanner.stop();
          }

          scanner.clear();

          if (scannerRef.current === scanner) {
            scannerRef.current = null;
          }
        }
      } catch (error) {
        console.error("Kamera konnte nicht gestartet werden:", error);

        if (!cancelled && session === scannerSessionRef.current) {
          showStatus(
            "error",
            error.message || "Kamera konnte nicht geöffnet werden."
          );

          setShowScanner(false);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      scannerSessionRef.current += 1;

      const oldScanner = scanner;

      if (scannerRef.current === oldScanner) {
        scannerRef.current = null;
      }

      const cleanup = async () => {
        try {
          if (startPromise) {
            await startPromise.catch(() => {});
          }

          if (oldScanner?.isScanning) {
            await oldScanner.stop();
          }

          oldScanner?.clear();
        } catch (error) {
          console.warn("Kamera-Cleanup:", error);
        }
      };

      cleanup();
    };
  }, [showScanner, showActivityModal, modalStep, showStatus]);

  // ==========================================
  // SCANNER STARTEN
  // ==========================================

  const startScanner = () => {
    if (!activity.trim()) {
      showStatus(
        "error",
        "Bitte zuerst eine Tätigkeit eingeben."
      );
      return;
    }

    scanLockedRef.current = false;
    setShowScanner(true);
  };

  // ==========================================
  // MODAL ÖFFNEN / SCHLIESSEN
  // ==========================================

  const openActivityModal = () => {
    if (saving || scanLockedRef.current) return;
    setActivity("");
    setModalStep(1);
    setShowScanner(false);
    scanLockedRef.current = false;
    setShowActivityModal(true);
    setSelectedProjectId("");
  };

  const capturePlannedAssignment = (assignment) => {
    if (saving || scanLockedRef.current) return;
    openActivityModal();
    setSelectedProjectId(String(assignment.site_id));
    setActiveTab("work");
  };

  const closeActivityModal = async () => {
    if (saving || scanLockedRef.current) return;

    if (showScanner) {
      await stopScanner();
    }

    setShowActivityModal(false);
    setModalStep(1);
  };

  // ==========================================
  // DATUM FORMATIEREN
  // ==========================================

  const formatDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Unbekannte Zeit";
    }

    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isToday = (value) => {
    const date = new Date(value);
    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  // ==========================================
  // STATISTIKEN
  // ==========================================

  const todayEntries = entries.filter((entry) =>
    isToday(entry.time)
  );

  const latestEntry = entries[0];

  const uniqueLocations = new Set(
    entries.map((entry) => entry.code).filter(Boolean)
  ).size;

  const currentDate = new Date().toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="qr-page">

      {/* ======================================
          HEADER
         ====================================== */}

      <header className="qr-header">
        <div className="qr-header-inner">

          <div className="qr-brand">
            <div className="qr-brand-logo">
              <img
                src={`${process.env.PUBLIC_URL}/Images/qroof-logo.png`}
                alt="QRoof"
                className="qr-logo"
              />
            </div>

            <div className="qr-brand-text">
              <span className="qr-eyebrow">
                QROOF WORKSPACE
              </span>

              <h1>Zeiterfassung</h1>
            </div>
          </div>

          <div className="qr-header-actions">

            <button
              type="button"
              className="qr-profile-button"
              onClick={() => setShowProfileModal(true)}
              title="Mein Profil"
            >
              <FaUserCircle />
              <span>Profil</span>
            </button>

            <button
              type="button"
              className="logout-button"
              onClick={onLogout}
              title="Abmelden"
            >
              <FaSignOutAlt />
              <span>Abmelden</span>
            </button>

          </div>
        </div>
      </header>

      {/* ======================================
          NAVIGATION DIREKT UNTER HEADER
         ====================================== */}

      <nav
        className="qr-navigation"
        aria-label="Hauptnavigation"
      >
        <div className="qr-navigation-inner" style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>

          <button
            type="button"
            className={`qr-nav-item ${
              activeTab === "overview" ? "active" : ""
            }`}
            onClick={() => setActiveTab("overview")}
            aria-current={
              activeTab === "overview" ? "page" : undefined
            }
          >
            <FaThLarge />
            <span>Übersicht</span>
          </button>

          <button
            type="button"
            className={`qr-nav-item ${
              activeTab === "work" ? "active" : ""
            }`}
            onClick={() => setActiveTab("work")}
            aria-current={
              activeTab === "work" ? "page" : undefined
            }
          >
            <FaClipboardList />
            <span>Arbeitsbereich</span>
          </button>

          {localStorage.getItem("environment") === "demo" && (
            <button type="button"
              className={`qr-nav-item ${activeTab === "assignments" ? "active" : ""}`}
              onClick={() => setActiveTab("assignments")}
              aria-current={activeTab === "assignments" ? "page" : undefined}>
              <FaCalendarDay />
              <span>Meine Termine</span>
            </button>
          )}

          <button
            type="button"
            className={`qr-nav-item ${
              activeTab === "history" ? "active" : ""
            }`}
            onClick={() => setActiveTab("history")}
            aria-current={
              activeTab === "history" ? "page" : undefined
            }
          >
            <FaClock />
            <span>Verlauf</span>

            <span className="qr-nav-count">
              {entries.length}
            </span>
          </button>

        </div>
      </nav>

      {/* ======================================
          MAIN
         ====================================== */}

      <main className="qr-content">

        {/* STATUS */}

        {message && (
          <div
            className={`status-message status-${message.type}`}
            role="status"
          >
            {message.type === "success" ? (
              <FaCheckCircle />
            ) : (
              <FaExclamationTriangle />
            )}

            <span>{message.text}</span>

            <button
              type="button"
              onClick={() => setMessage(null)}
              aria-label="Meldung schließen"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* ==================================
            TAB 1 – ÜBERSICHT
           ================================== */}

        {activeTab === "assignments" && localStorage.getItem("environment") === "demo" && (
          <EmployeeAssignments onCapture={capturePlannedAssignment} />
        )}

        {activeTab === "overview" && (
          <section className="qr-tab-panel qr-overview-panel">
            {localStorage.getItem("environment") === "demo" && (
              <EmployeeAgenda onCapture={capturePlannedAssignment} onOpen={() => setActiveTab("assignments")} onTimerSaved={fetchEntries} />
            )}


            <div className="qr-page-heading">
              <div>
                <span className="qr-section-kicker">
                  DEIN DASHBOARD
                </span>

                <h2>Übersicht</h2>

                <p>
                  Alle wichtigen Informationen zu deinen
                  erfassten Tätigkeiten auf einen Blick.
                </p>
              </div>

              <div className="qr-date-pill">
                <FaCalendarDay />
                <span>{currentDate}</span>
              </div>
            </div>

            {/* STATISTIKEN */}

            <div className="qr-stats-grid">

              <div className="qr-stat-card">
                <div className="qr-stat-icon blue">
                  <FaClipboardList />
                </div>

                <div className="qr-stat-content">
                  <span>Alle Erfassungen</span>
                  <strong>{entries.length}</strong>
                  <small>Gespeicherte Tätigkeiten</small>
                </div>
              </div>

              <div className="qr-stat-card">
                <div className="qr-stat-icon green">
                  <FaCheckCircle />
                </div>

                <div className="qr-stat-content">
                  <span>Heute erfasst</span>
                  <strong>{todayEntries.length}</strong>
                  <small>Einträge am heutigen Tag</small>
                </div>
              </div>

              <div className="qr-stat-card">
                <div className="qr-stat-icon purple">
                  <FaMapMarkerAlt />
                </div>

                <div className="qr-stat-content">
                  <span>Arbeitsorte</span>
                  <strong>{uniqueLocations}</strong>
                  <small>Unterschiedliche Einsatzorte</small>
                </div>
              </div>

            </div>

            {/* LETZTE ERFASSUNG */}

            <div className="qr-overview-section-heading">
              <div>
                <span className="qr-section-kicker">
                  AKTUELLE AKTIVITÄT
                </span>
                <h3>Letzte Erfassung</h3>
              </div>

              <button
                type="button"
                className="qr-text-button"
                onClick={() => setActiveTab("history")}
              >
                Verlauf ansehen
                <FaArrowRight />
              </button>
            </div>

            {latestEntry ? (
              <div className="qr-overview-latest">

                <div className="qr-overview-latest-icon">
                  <FaMapMarkerAlt />
                </div>

                <div className="qr-overview-latest-content">
                  <span>LETZTER ARBEITSORT</span>

                  <h4>{latestEntry.code}</h4>

                  <p>{latestEntry.activity}</p>

                  <div className="qr-overview-latest-date">
                    <FaClock />
                    {formatDate(latestEntry.time)}
                  </div>
                </div>


<div
  className="qr-success-pill"
  style={{
    color: getEntryStatus(latestEntry).color,
    backgroundColor: getEntryStatus(latestEntry).background,
  }}
>
  <FaCheckCircle />
  {getEntryStatus(latestEntry).label}
</div>


              </div>
            ) : (
              <div className="qr-empty-overview">
                <FaQrcode />
                <h4>Noch keine Erfassungen</h4>
                <p>
                  Deine letzte Tätigkeit wird hier angezeigt,
                  sobald du einen QR-Code gescannt hast.
                </p>

                <button
                  type="button"
                  className="qr-primary-button"
                  onClick={() => setActiveTab("work")}
                >
                  Zum Arbeitsbereich
                  <FaArrowRight />
                </button>
              </div>
            )}

            {/* SCHNELLZUGRIFF */}

            <div className="qr-quick-access">
              <div>
                <span className="qr-section-kicker">
                  SCHNELLZUGRIFF
                </span>

                <h3>Neue Tätigkeit erfassen</h3>

                <p>
                  Du möchtest einen weiteren Einsatz
                  dokumentieren?
                </p>
              </div>

              <button
                type="button"
                className="qr-primary-button"
                onClick={() => setActiveTab("work")}
              >
                <FaPlus />
                Arbeitsbereich öffnen
                <FaArrowRight />
              </button>
            </div>

          </section>
        )}

        {/* ==================================
            TAB 2 – ARBEITSBEREICH
           ================================== */}

        {activeTab === "work" && (
          <section className="qr-tab-panel qr-work-panel">

            <div className="qr-page-heading">
              <div>
                <span className="qr-section-kicker">
                  QR-ZEITERFASSUNG
                </span>

                <h2>Arbeitsbereich</h2>

                <p>
                  Erfasse deine Tätigkeit und scanne
                  anschließend den QR-Code am Einsatzort.
                </p>
              </div>
            </div>

            <div className="qr-work-card">

              <div className="qr-work-decoration">
                <FaQrcode />
              </div>

              <div className="qr-work-content">

                <div className="qr-work-icon">
                  <FaQrcode />
                </div>

                <span className="qr-work-kicker">
                  NEUE ERFASSUNG
                </span>

                <h3>Tätigkeit hinzufügen</h3>

                <p>
                  Dokumentiere deine durchgeführte Arbeit
                  und ordne sie durch das Scannen eines
                  QR-Codes dem richtigen Arbeitsort zu.
                </p>

                <button
                  type="button"
                  className="qr-primary-button"
                  onClick={openActivityModal}
                  disabled={saving}
                >
                  <FaPlus />
                  Hinzufügen
                  <FaArrowRight />
                </button>

              </div>
            </div>

            <div className="qr-work-help">

              <div className="qr-work-help-icon">
                <FaShieldAlt />
              </div>

              <div>
                <strong>So funktioniert die Erfassung</strong>

                <p>
                  Tätigkeit eingeben, QR-Code scannen und
                  automatisch im Verlauf speichern.
                </p>
              </div>

            </div>

          </section>
        )}

        {/* ==================================
            TAB 3 – VERLAUF
           ================================== */}

        {activeTab === "history" && (
          <section className="qr-tab-panel qr-history-panel">

            <div className="qr-page-heading">
              <div>
                <span className="qr-section-kicker">
                  DEINE DOKUMENTATION
                </span>

                <h2>Verlauf</h2>

                <p>
                  Hier findest du alle bisher
                  erfassten Tätigkeiten.
                </p>
              </div>

              <span className="qr-history-count">
                {entries.length} Einträge
              </span>
            </div>

            {loadingEntries ? (
              <div className="qr-empty-state">
                <div className="loading-spinner" />
                <p>Einträge werden geladen ...</p>
              </div>
            ) : entries.length === 0 ? (
              <div className="qr-empty-state">
                <FaClipboardList />
                <h3>Noch keine Einträge</h3>
                <p>
                  Sobald du deine erste Tätigkeit erfasst,
                  erscheint sie hier.
                </p>
              </div>
            ) : (
              <div className="qr-entries-grid">

                {entries.map((entry, index) => (
                  <article
                    className="qr-entry-card"
                    key={
                      entry.id ||
                      `${entry.time}-${entry.code}-${index}`
                    }
                  >
                    <div className="qr-entry-top">

                      <div className="qr-entry-location-icon">
                        <FaMapMarkerAlt />
                      </div>

                      <div className="qr-entry-location">
                        <span>ARBEITSORT</span>
                        <h3>{entry.code}</h3>
                      </div>

<div className="qr-entry-status-group">
  <span
    className={`qr-entry-status status-${getEntryStatus(entry).className}`}
  >
    <FaCheckCircle />
    {getEntryStatus(entry).label}
  </span>

  {getEntryStatus(entry).edited && (
    <span className="qr-entry-status status-bearbeitet">
      Bearbeitet
    </span>
  )}
</div>

                    </div>

                    <div className="qr-entry-description">
                      {entry.activity}
                    </div>
{localStorage.getItem("environment") === "demo" &&
  entry.status === "erfasst" &&
  Number(entry.user_id) === Number(userId) && (
    <button
      type="button"
      className="qr-entry-edit-button"
      onClick={() => setEditingEntry(entry)}
    >
      Eintrag bearbeiten
    </button>
  )}
                    <div className="qr-entry-footer">
                      <FaClock />
                      <span>{formatDate(entry.time)}</span>
                    </div>

                  </article>
                ))}

              </div>
            )}

          </section>
        )}

      </main>
{editingEntry && (
  <WorkerEntryEdit
    key={editingEntry.id}
    entry={editingEntry}
    onClose={() => setEditingEntry(null)}
    onSaved={changed => {
      setEditingEntry(null);
      showStatus(
        "success",
        changed ? "Eintrag gespeichert." : "Keine Änderungen vorhanden."
      );
      fetchEntries();
    }}
  />
)}
      {/* ======================================
          PROFIL MODAL
         ====================================== */}

      {showProfileModal && (
        <div className="activity-modal-overlay">

          <div
            className="qr-profile-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-profile-title"
          >

            <div className="qr-profile-modal-header">

              <div>
                <span className="qr-section-kicker">
                  MEIN KONTO
                </span>

                <h2 id="qr-profile-title">
                  Benutzerprofil
                </h2>
              </div>

              <button
                type="button"
                className="qr-modal-close"
                onClick={() => setShowProfileModal(false)}
                aria-label="Profil schließen"
              >
                <FaTimes />
              </button>

            </div>

            <div className="qr-profile-body">

              <div className="qr-profile-avatar">
                <FaUserCircle />
              </div>

              <h3>{username}</h3>

              <p>QRoof Benutzerkonto</p>

              <div className="qr-profile-info">

                <div className="qr-profile-info-row">
                  <div className="qr-profile-info-icon">
                    <FaIdBadge />
                  </div>

                  <div>
                    <span>BENUTZER-ID</span>
                    <strong>{userId || "Unbekannt"}</strong>
                  </div>
                </div>

                <div className="qr-profile-info-row">
                  <div className="qr-profile-info-icon">
                    <FaUserCircle />
                  </div>

                  <div>
                    <span>BENUTZERNAME</span>
                    <strong>{username}</strong>
                  </div>
                </div>

                <div className="qr-profile-info-row">
                  <div className="qr-profile-info-icon">
                    <FaEnvelope />
                  </div>

                  <div>
                    <span>E-MAIL</span>
                    <strong>{userEmail}</strong>
                  </div>
                </div>

              </div>

              <button
                type="button"
                className="qr-modal-secondary"
                onClick={() => setShowProfileModal(false)}
              >
                Schließen
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ======================================
          TÄTIGKEIT / QR MODAL
         ====================================== */}

      {showActivityModal && (
        <div className="activity-modal-overlay">

          <div
            className="activity-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-modal-title"
          >

            <div className="qr-modal-header">

              <div className="qr-modal-heading">

                <div className="qr-modal-icon">
                  <FaQrcode />
                </div>

                <div>
                  <span>QROOF · NEUE ERFASSUNG</span>

                  <h2 id="activity-modal-title">
                    {modalStep === 1
                      ? "Tätigkeit erfassen"
                      : "Arbeitsort scannen"}
                  </h2>
                </div>

              </div>

              <button
                type="button"
                className="qr-modal-close"
                onClick={closeActivityModal}
                disabled={saving}
                aria-label="Schließen"
              >
                <FaTimes />
              </button>

            </div>

            {/* FORTSCHRITT */}

            <div className="qr-modal-progress">

              <div className="qr-progress-label">
                <span>
                  {modalStep === 1
                    ? "Tätigkeitsbeschreibung"
                    : "QR-Code-Erfassung"}
                </span>

                <strong>Schritt {modalStep} / 2</strong>
              </div>

              <div className="qr-progress-bars">
                <span className="active" />

                <span
                  className={modalStep === 2 ? "active" : ""}
                />
              </div>

            </div>

            {/* SCHRITT 1 */}

            {modalStep === 1 ? (
              <form
                className="qr-modal-content"
                onSubmit={(event) => {
                  event.preventDefault();

                  if (saving || scanLockedRef.current || !activity.trim()) return;

                  try {
                    createWorkTimePayload();
                    setWorkTimeError("");
                  } catch (error) {
                    setWorkTimeError(error.message);
                    return;
                  }
                  setActivity(activity.trim());
                  setModalStep(2);
                }}
              >

                <div className="qr-modal-intro">

                  <div className="qr-modal-step-icon">
                    <FaClipboardList />
                  </div>

                  <h3>Was hast du erledigt?</h3>

                  <p>
                    Beschreibe deine durchgeführte Arbeit.
                    Wähle anschließend ein Projekt aus oder scanne
                    den QR-Code am Einsatzort.
                  </p>

                </div>

                <label
                  className="qr-input-label"
                  htmlFor="modalActivityInput"
                >
                  Tätigkeitsbeschreibung
                </label>

                <textarea
                  id="modalActivityInput"
                  className="qr-activity-input"
                  value={activity}
                  onChange={(event) =>
                    setActivity(event.target.value)
                  }
                  placeholder="z. B. Dachrinne gereinigt, Wartung durchgeführt ..."
                  maxLength={500}
                  rows={5}
                  required
                  autoFocus
                />

                <div className="qr-input-footer">
                  <span>
                    Beschreibe die Tätigkeit möglichst genau.
                  </span>

                  <span>{activity.length}/500</span>
                </div>
                {localStorage.getItem("environment") === "demo" && (
                  <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: "20px 0", display: "grid", gap: "14px" }}>
                    <legend style={{ fontWeight: 700, marginBottom: "12px" }}>Tatsächliche Arbeitszeit</legend>
                    <small>Alle Angaben in Berliner Ortszeit. Geplante Zeiten werden nicht automatisch übernommen.</small>
                    <label className="qr-input-label" htmlFor="work-started-at">Beginn *</label>
                    <input id="work-started-at" type="datetime-local" required value={workStartedAt}
                      onChange={event => { setWorkStartedAt(event.target.value); setWorkTimeError(""); }}
                      style={{ padding: "12px", border: "1px solid #ccd9e5", borderRadius: "10px", font: "inherit", width: "100%", boxSizing: "border-box" }} />
                    <label className="qr-input-label" htmlFor="work-ended-at">Ende *</label>
                    <input id="work-ended-at" type="datetime-local" required value={workEndedAt}
                      onChange={event => { setWorkEndedAt(event.target.value); setWorkTimeError(""); }}
                      style={{ padding: "12px", border: "1px solid #ccd9e5", borderRadius: "10px", font: "inherit", width: "100%", boxSizing: "border-box" }} />
                    <label className="qr-input-label" htmlFor="work-break-minutes">Pause in Minuten *</label>
                    <input id="work-break-minutes" type="number" min="0" max="1439" step="1" required value={workBreakMinutes}
                      onChange={event => { setWorkBreakMinutes(event.target.value); setWorkTimeError(""); }}
                      style={{ padding: "12px", border: "1px solid #ccd9e5", borderRadius: "10px", font: "inherit", width: "100%", boxSizing: "border-box" }} />
                    <p role="status" aria-live="polite" style={{ margin: 0 }}>
                      Nettoarbeitszeit: {netWorkMinutes === null ? "Bitte vollständige Zeiten eingeben" :
                        `${Math.floor(netWorkMinutes / 60)} Std. ${netWorkMinutes % 60} Min.`}
                    </p>
                  </fieldset>
                )}
                {workTimeError && <p role="alert" style={{ color: "#a52c2c" }}>{workTimeError}</p>}
{localStorage.getItem("environment") === "demo" && (
  <div style={{ margin: "20px 0" }}>
    <label
      className="qr-input-label"
      htmlFor="entry-project-select"
    >
      Alternativ: Projekt auswählen
    </label>

    <select
      id="entry-project-select"
      value={selectedProjectId}
      onChange={(event) => setSelectedProjectId(event.target.value)}
      disabled={saving || projectsLoading}
      style={{
        width: "100%",
        padding: "12px",
        margin: "10px 0",
        border: "1px solid #ccd9e5",
        borderRadius: "10px",
        font: "inherit",
      }}
    >
      <option value="">
        {projectsLoading ? "Projekte werden geladen …" : "Projekt auswählen"}
      </option>

      {projects.map((project) => (
        <option key={project.id} value={project.id}>
          {project.customer_name} · {project.project_name}
        </option>
      ))}
    </select>

    {projectsError && <p role="alert">{projectsError}</p>}

    {!projectsLoading && !projectsError && projects.length === 0 && (
      <p>Keine aktiven Projekte vorhanden.</p>
    )}

    <button
      type="button"
      className="qr-modal-primary"
      disabled={
        saving ||
        projectsLoading ||
        !activity.trim() ||
        !selectedProjectId
      }
      onClick={saveSelectedProject}
    >
      {saving ? "Wird gespeichert …" : "Für ausgewähltes Projekt erfassen"}
    </button>
  </div>
)}
                <button
                  type="submit"
                  className="qr-modal-primary"
                  disabled={saving || !activity.trim()}
                >
                  Weiter zum QR-Code
                  <FaArrowRight />
                </button>

              </form>
            ) : (

              /* SCHRITT 2 */

              <div className="qr-modal-content">

                <div className="qr-activity-summary">

                  <div className="qr-summary-icon">
                    <FaCheckCircle />
                  </div>

                  <div>
                    <span>AKTUELLE TÄTIGKEIT</span>
                    <strong>{activity}</strong>
                  </div>

                </div>

                <div className="qr-scan-heading">
                  <h3>QR-Code scannen</h3>
                  {localStorage.getItem("environment") === "demo" && (
                    <p>Arbeitszeit: {workStartedAt.replace("T", " ")} bis {workEndedAt.replace("T", " ")}; Pause: {workBreakMinutes} Minuten</p>
                  )}
                  {workTimeError && <p role="alert" style={{ color: "#a52c2c" }}>{workTimeError}</p>}

                  <p>
                    Halte den QR-Code vollständig
                    in den markierten Bereich.
                  </p>
                </div>

                {!showScanner ? (
                  <div className="qr-camera-placeholder">

                    <div className="qr-camera-placeholder-icon">
                      <FaCamera />
                    </div>

                    <strong>Kamera bereit</strong>

                    <span>
                      Starte die Kamera, um deinen
                      Arbeitsort zu erfassen.
                    </span>

                  </div>
                ) : (
                  <div className="qr-camera-frame">

                    <div
                      id="qr-reader"
                      className="scanner-reader"
                    />

                    <div
                      className="qr-camera-corners"
                      aria-hidden="true"
                    >
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>

                    <div
                      className="qr-camera-scanline"
                      aria-hidden="true"
                    />

                    <div className="qr-camera-hint">
                      <FaQrcode />
                      QR-Code hier positionieren
                    </div>

                  </div>
                )}

                {!showScanner ? (
                  <button
                    type="button"
                    className="qr-modal-primary"
                    onClick={startScanner}
                    disabled={saving}
                  >
                    <FaCamera />
                    Kamera starten
                    <FaArrowRight />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="qr-modal-secondary"
                    onClick={stopScanner}
                    disabled={saving}
                  >
                    <FaTimes />
                    Kamera schließen
                  </button>
                )}

                {!showScanner && (
                  <button
                    type="button"
                    className="qr-modal-back"
                    onClick={() => setModalStep(1)}
                    disabled={saving}
                  >
                    <FaArrowLeft />
                    Tätigkeit bearbeiten
                  </button>
                )}

                {saving && (
                  <p className="qr-saving-message">
                    Eintrag wird gespeichert ...
                  </p>
                )}

              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

export default QRPage;